import type {User} from "../model/User";

export interface PushNotificationRegistration {
    id?: number | string;
    token?: string;
    platform?: string;
    userAgent?: string;
    enabled?: boolean;
    softDeleted?: boolean;
    createdOn?: string;
    updatedOn?: string;
    lastSeenAt?: string;
}

export interface PushRegistrationSummary {
    registrations: PushNotificationRegistration[];
    totalRegistrations: number;
    enabledRegistrations: number;
    platforms: string[];
    latestSeenAt: string | null;
}

const PUSH_REGISTRATION_KEYS = [
    'pushNotificationRegistrations',
    'pushNotificationTokens',
    'pushNotifications',
    'notificationTokens',
    'registeredPushDevices',
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const asOptionalString = (value: unknown): string | undefined => {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
};

const asOptionalNumberOrString = (value: unknown): number | string | undefined => {
    if (typeof value === 'number' || typeof value === 'string') {
        return value;
    }

    return undefined;
};

const asOptionalBoolean = (value: unknown): boolean | undefined => {
    return typeof value === 'boolean' ? value : undefined;
};

const normalizeRegistration = (value: unknown): PushNotificationRegistration | null => {
    if (!isRecord(value)) {
        return null;
    }

    return {
        id: asOptionalNumberOrString(value.id),
        token: asOptionalString(value.token),
        platform: asOptionalString(value.platform),
        userAgent: asOptionalString(value.userAgent),
        enabled: asOptionalBoolean(value.enabled),
        softDeleted: asOptionalBoolean(value.softDeleted),
        createdOn: asOptionalString(value.createdOn),
        updatedOn: asOptionalString(value.updatedOn),
        lastSeenAt: asOptionalString(value.lastSeenAt),
    };
};

const getRawRegistrationCollection = (user: User): {isDataAvailable: boolean; registrations: PushNotificationRegistration[]} => {
    const pushAwareUser = user as User & Record<string, unknown>;
    let isDataAvailable = false;
    const registrations: PushNotificationRegistration[] = [];

    PUSH_REGISTRATION_KEYS.forEach((key) => {
        if (!(key in pushAwareUser)) {
            return;
        }

        isDataAvailable = true;
        const rawValue = pushAwareUser[key];

        if (Array.isArray(rawValue)) {
            rawValue.forEach((entry) => {
                const normalized = normalizeRegistration(entry);
                if (normalized) {
                    registrations.push(normalized);
                }
            });
            return;
        }

        const normalized = normalizeRegistration(rawValue);
        if (normalized) {
            registrations.push(normalized);
        }
    });

    return {isDataAvailable, registrations};
};

export const isPushRegistrationActive = (registration: PushNotificationRegistration): boolean => {
    return registration.enabled !== false && registration.softDeleted !== true;
};

export const getPushRegistrationSummary = (user?: User): PushRegistrationSummary => {
    if (!user) {
        return {
            registrations: [],
            totalRegistrations: 0,
            enabledRegistrations: 0,
            platforms: [],
            latestSeenAt: null,
        };
    }

    const {isDataAvailable, registrations} = getRawRegistrationCollection(user);
    const enabledRegistrations = registrations.filter(isPushRegistrationActive).length;
    const platforms = Array.from(new Set(registrations.map((registration) => registration.platform?.toLowerCase()).filter(Boolean))) as string[];
    const latestSeenAt = registrations
        .map((registration) => registration.lastSeenAt)
        .filter((value): value is string => Boolean(value))
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] ?? null;

    return {
        registrations,
        totalRegistrations: registrations.length,
        enabledRegistrations,
        platforms,
        latestSeenAt,
    };
};